import { localStorage as safeLocalStorage } from 'utils/browserStorage';
import React, { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { isPhoneNotVerifiedError } from 'utils/phoneVerificationGate';
import { createPortal } from 'react-dom';
import { useHistory } from 'react-router-dom';
import dayjs from 'dayjs';
import relativeTime from 'dayjs/plugin/relativeTime';
import calendar from 'dayjs/plugin/calendar';
import Api from '../../api';
import {
  SHIFT_UNAVAILABLE_MESSAGE,
  extractNotificationJobId,
  isJobUnavailableForNotificationNav,
  resolveDentistOfferNotificationRoute,
} from '../../utils/notificationJobGate';
import styles from './NotificationBell.module.scss';

dayjs.extend(relativeTime);
dayjs.extend(calendar);

type NotificationCTA = {
  label: string;
  route?: string;
  params?: Record<string, any>;
};

type NotificationPayload = {
  route?: string;
  params?: Record<string, any>;
  cta?: NotificationCTA[];
  chips?: string[];
  preview?: string;
  analytics?: string;
  snoozeable?: boolean;
  color?: string;
  source?: string;
  broadcastId?: number;
};

type NotificationRecord = {
  id: number;
  type: string;
  title: string;
  message?: string | null;
  data?: NotificationPayload | string | null;
  jobId?: number | null;
  isRead: number | boolean;
  snoozedUntil?: string | null;
  createdAt: string;
};

const normalizeData = (raw: NotificationRecord['data']): NotificationPayload => {
  if (!raw) return {};
  if (typeof raw === 'object') return raw as NotificationPayload;
  try {
    return JSON.parse(raw) as NotificationPayload;
  } catch {
    return {};
  }
};

const formatTime = (createdAt: string): string => {
  return dayjs(createdAt).fromNow();
};

const getNotificationMessage = (notification: NotificationRecord, payload?: NotificationPayload): string => {
  const message = typeof notification.message === 'string' ? notification.message.trim() : '';
  if (message) return message;
  const preview = typeof payload?.preview === 'string' ? payload.preview.trim() : '';
  if (preview) return preview;
  return 'Tap to view details.';
};

const getDistinctPreview = (
  notification: NotificationRecord,
  payload?: NotificationPayload,
): string | null => {
  const preview = typeof payload?.preview === 'string' ? payload.preview.trim() : '';
  const message = typeof notification.message === 'string' ? notification.message.trim() : '';
  return preview && preview !== message ? preview : null;
};

const getGroupLabel = (createdAt: string): string => {
  const date = dayjs(createdAt);
  const now = dayjs();
  
  if (date.isSame(now, 'day')) return 'Today';
  if (date.isSame(now.subtract(1, 'day'), 'day')) return 'Yesterday';
  
  return date.format('MMMM D');
};

const renderIcon = (backgroundColor: string, content: React.ReactNode, notificationType?: string) => (
  <div
    className={styles.iconColumn}
    style={{ backgroundColor }}
    data-notification-icon={notificationType}
  >
    {content}
  </div>
);

// Icon mapping based on type
const getIconForType = (type: string, colorHint?: string) => {
  const normalizedType = String(type || '').toUpperCase();

  switch (normalizedType) {
    case 'MEMBERSHIP_COURSE_REQUIRED': {
      const courseColor = colorHint || '#D97706';
      return renderIcon(
        courseColor,
        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2">
          <path d="M4 19.5A2.5 2.5 0 0 1 6.5 17H20"></path>
          <path d="M6.5 2H20v20H6.5A2.5 2.5 0 0 1 4 19.5v-15A2.5 2.5 0 0 1 6.5 2z"></path>
          <line x1="9" y1="7" x2="16" y2="7"></line>
        </svg>,
        normalizedType,
      );
    }
    case 'MEMBERSHIP_REINSTATED': {
      const reinstatedColor = colorHint || '#13CA82';
      return renderIcon(
        reinstatedColor,
        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2">
          <circle cx="12" cy="12" r="9"></circle>
          <polyline points="8 12 11 15 16 9"></polyline>
        </svg>,
        normalizedType,
      );
    }
    case 'MEMBERSHIP_REENTRY_COMPLETE': {
      const completeColor = colorHint || '#13CA82';
      return renderIcon(
        completeColor,
        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2">
          <polygon points="12 2 15 8.5 22 9.3 17 14 18.4 21 12 17.5 5.6 21 7 14 2 9.3 9 8.5 12 2"></polygon>
        </svg>,
        normalizedType,
      );
    }
    case 'MEMBERSHIP_NOT_A_FIT': {
      const supportColor = colorHint || '#0B4598';
      return renderIcon(
        supportColor,
        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2">
          <path d="M21 15a3 3 0 0 1-3 3H8l-5 4V6a3 3 0 0 1 3-3h12a3 3 0 0 1 3 3z"></path>
          <line x1="8" y1="9" x2="16" y2="9"></line>
          <line x1="8" y1="13" x2="13" y2="13"></line>
        </svg>,
        normalizedType,
      );
    }
    case 'SHIFT_CONFIRMED':
    case 'OFFER_ACCEPTED':
    case 'OFFER_ACCEPTED_HYG': {
      const confirmedColor = colorHint || '#13CA82';
      return renderIcon(
        confirmedColor,
        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2">
          <polyline points="20 6 9 17 4 12"></polyline>
        </svg>
      );
    }
    case 'BOOKED_SHIFT_TIME_UPDATED': {
      const timeUpdatedColor = colorHint || '#0B4598';
      return renderIcon(
        timeUpdatedColor,
        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2">
          <circle cx="12" cy="12" r="9"></circle>
          <polyline points="12 7 12 12 16 14"></polyline>
        </svg>
      );
    }
    case 'SHIFT_POSTED':
    case 'NEW_SHIFT':
    case 'TEMP_JOB':
    case 'SHIFT_AVAILABLE': {
      const shiftPostedColor = colorHint || '#0B4598';
      return renderIcon(
        shiftPostedColor,
        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2">
          <rect x="3" y="4" width="18" height="18" rx="2"></rect>
          <line x1="16" y1="2" x2="16" y2="6"></line>
          <line x1="8" y1="2" x2="8" y2="6"></line>
          <line x1="3" y1="10" x2="21" y2="10"></line>
          <line x1="12" y1="13" x2="12" y2="19"></line>
          <line x1="9" y1="16" x2="15" y2="16"></line>
        </svg>
      );
    }
    case 'SHIFT_CANCELLED': {
      const cancelledColor = colorHint || '#FB7672';
      return renderIcon(
        cancelledColor,
        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2">
          <line x1="18" y1="6" x2="6" y2="18"></line>
          <line x1="6" y1="6" x2="18" y2="18"></line>
        </svg>
      );
    }
    case 'SHIFT_CANCELLED_BY_TALENT':
    case 'SHIFT_CANCELLED_BY_DO':
    case 'SHIFT_CANCELLED_BY_DENTIST':
    case 'SHIFT_CANCELLED_BY_OFFICE':
    case 'TALENT_CANCELLED_SHIFT': {
      const cancelledByOtherColor = colorHint || '#FB7672';
      return renderIcon(
        cancelledByOtherColor,
        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2">
          <path d="M9 3H5a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-4"></path>
          <line x1="9" y1="9" x2="15" y2="15"></line>
          <line x1="15" y1="9" x2="9" y2="15"></line>
          <line x1="15" y1="3" x2="21" y2="3"></line>
        </svg>
      );
    }
    case 'OFFER_DECLINED': {
      const declinedColor = colorHint || '#FB7672';
      return renderIcon(
        declinedColor,
        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2">
          <circle cx="12" cy="12" r="10"></circle>
          <line x1="4.93" y1="4.93" x2="19.07" y2="19.07"></line>
        </svg>
      );
    }
    case 'NEW_OFFER':
    case 'OFFER_RECEIVED': {
      const offerColor = colorHint || '#0B4598';
      return renderIcon(
        offerColor,
        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2">
          <circle cx="12" cy="12" r="8"></circle>
          <circle cx="12" cy="12" r="4"></circle>
          <circle cx="12" cy="12" r="1"></circle>
        </svg>
      );
    }
    case 'APPLICATION_VIEWED': {
      const viewedColor = colorHint || '#0B4598';
      return renderIcon(
        viewedColor,
        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2">
          <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"></path>
          <circle cx="12" cy="12" r="3"></circle>
        </svg>
      );
    }
    case 'APPLICATION_STATUS': {
      const applicationColor = colorHint || '#0B4598';
      return renderIcon(
        applicationColor,
        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2">
          <path d="M9 2h6l4 4v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h2"></path>
          <polyline points="9 12 11 14 15 10"></polyline>
          <line x1="9" y1="7" x2="15" y2="7"></line>
        </svg>
      );
    }
    case 'CHAT_MESSAGE': {
      const chatColor = colorHint || '#0B4598';
      return renderIcon(
        chatColor,
        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2">
          <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"></path>
        </svg>
      );
    }
    case 'NEW_MESSAGE': {
      const newMessageColor = colorHint || '#0B4598';
      return renderIcon(
        newMessageColor,
        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2">
          <path d="M21 11.5a8.5 8.5 0 0 1-8.5 8.5H7l-4 3V11.5A8.5 8.5 0 0 1 11.5 3H12"></path>
          <line x1="8" y1="11" x2="16" y2="11"></line>
          <line x1="8" y1="15" x2="13" y2="15"></line>
        </svg>
      );
    }
    case 'NEW_INVOICE': {
      const invoiceColor = colorHint || '#0B4598';
      return renderIcon(
        invoiceColor,
        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2">
          <path d="M7 3h10v18l-2-1-2 1-2-1-2 1-2-1-2 1V5a2 2 0 0 1 2-2z"></path>
          <line x1="9" y1="8" x2="15" y2="8"></line>
          <line x1="9" y1="12" x2="15" y2="12"></line>
          <line x1="9" y1="16" x2="13" y2="16"></line>
        </svg>
      );
    }
    case 'INVOICE_REMINDER': {
      const invoiceColor = colorHint || '#0B4598';
      return renderIcon(
        invoiceColor,
        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2">
          <circle cx="12" cy="12" r="10"></circle>
          <polyline points="12 6 12 12 16 14"></polyline>
        </svg>
      );
    }
    case 'ADMIN_BROADCAST': {
      // T2-6151: TempStars / admin Message Users — distinct cobalt + megaphone
      const adminColor = colorHint || '#0B4598';
      return renderIcon(
        adminColor,
        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2">
          <path d="M3 11v2a1 1 0 0 0 1 1h2l5 4V6L6 10H4a1 1 0 0 0-1 1z"></path>
          <path d="M16 8.5a4.5 4.5 0 0 1 0 7"></path>
          <path d="M18.5 6a8 8 0 0 1 0 12"></path>
        </svg>,
        'ADMIN_BROADCAST',
      );
    }
    default:
      return renderIcon(
        colorHint || '#999',
        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2">
          <circle cx="12" cy="12" r="10"></circle>
          <line x1="12" y1="16" x2="12" y2="12"></line>
          <line x1="12" y1="8" x2="12.01" y2="8"></line>
        </svg>
      );
  }
};

const isAdminBroadcastNotification = (
  notification: NotificationRecord,
  payload?: NotificationPayload,
): boolean => {
  const type = String(notification.type || '').toUpperCase();
  if (type === 'ADMIN_BROADCAST') return true;
  return payload?.source === 'admin_broadcast';
};

/** T2-6151: clamp long admin messages to ~3 lines with Read more / Read less. */
const AdminBroadcastMessage: React.FC<{ message: string }> = ({ message }) => {
  const [expanded, setExpanded] = useState(false);
  const [needsToggle, setNeedsToggle] = useState(false);
  const textRef = useRef<HTMLDivElement | null>(null);

  useLayoutEffect(() => {
    if (expanded) return;
    const el = textRef.current;
    if (!el) return;
    // Measure only while clamped so short messages do not show Read more.
    setNeedsToggle(el.scrollHeight > el.clientHeight + 1);
  }, [message, expanded]);

  return (
    <div className={styles.adminBroadcastMessage}>
      <div
        ref={textRef}
        className={`${styles.message} ${styles.adminBroadcastClamp} ${
          expanded ? styles.adminBroadcastExpanded : ''
        }`}
      >
        {message}
      </div>
      {needsToggle && (
        <button
          type="button"
          className={styles.readMoreToggle}
          aria-expanded={expanded}
          onClick={(e) => {
            e.stopPropagation();
            setExpanded((prev) => !prev);
          }}
        >
          {expanded ? 'Read less' : 'Read more'}
        </button>
      )}
    </div>
  );
};

const SHIFT_CANCELLED_SNACKBAR_TYPES = new Set([
  'SHIFT_CANCELLED',
  'SHIFT_CANCELLED_BY_DO',
  'SHIFT_CANCELLED_BY_DENTIST',
  'SHIFT_CANCELLED_BY_OFFICE',
]);

const NotificationBell: React.FC = () => {
  const history = useHistory();
  const [unreadCount, setUnreadCount] = useState(0);
  const [isOpen, setIsOpen] = useState(false);
  const [notifications, setNotifications] = useState<NotificationRecord[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [shiftsAvailableCache, setShiftsAvailableCache] = useState<Record<string, boolean>>({});
  const [snackbar, setSnackbar] = useState<string | null>(null);
  const [liveToast, setLiveToast] = useState<NotificationRecord | null>(null);
  const snackbarTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const liveToastTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const wrapperRef = useRef<HTMLDivElement | null>(null);
  const panelRef = useRef<HTMLDivElement | null>(null);
  const bellButtonRef = useRef<HTMLButtonElement | null>(null);
  const [panelFixedStyle, setPanelFixedStyle] = useState<React.CSSProperties | null>(null);
  const recentRealtimeNotificationIds = useRef<Set<number>>(new Set());
  const unreadCountRef = useRef(0);
  const livePulseTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const livePulseFrame = useRef<number | null>(null);
  const [isLivePulsing, setIsLivePulsing] = useState(false);

  const resolveCurrentUserId = useCallback((): number | null => {
    try {
      return (window as any)?.TempStars?.User?.getCurrentUser?.()?.id ?? null;
    } catch {
      return null;
    }
  }, []);

  const [currentUserId, setCurrentUserId] = useState<number | null>(() => resolveCurrentUserId());

  const resolveSocketJwtToken = useCallback(async (): Promise<string | null> => {
    try {
      const ts = (window as any)?.TempStars;
      if (typeof ts?._socketJwtToken === 'string' && ts._socketJwtToken) {
        return ts._socketJwtToken;
      }

      const storedAuth = await ts?.Storage?.get?.('userAuth');
      if (typeof storedAuth?.jwtToken === 'string' && storedAuth.jwtToken) {
        return storedAuth.jwtToken;
      }
      if (typeof storedAuth?.token === 'string' && storedAuth.token) {
        return storedAuth.token;
      }

      const rawAuth = safeLocalStorage?.getItem?.('userAuth');
      if (!rawAuth) return null;
      const parsedAuth = JSON.parse(rawAuth);
      return parsedAuth?.jwtToken || parsedAuth?.token || null;
    } catch {
      return null;
    }
  }, []);

  const ensureShellSocketConnected = useCallback(async () => {
    try {
      const ts = (window as any)?.TempStars;
      const socket = ts?.socket;
      const readyState = socket?.io?.readyState;
      if (socket?.connected || readyState === 'opening' || readyState === 'open') {
        return;
      }

      const token = await resolveSocketJwtToken();
      if (token && typeof ts?.App?.connectSocket === 'function') {
        ts.App.connectSocket(token);
      }
    } catch (err) {
      console.warn('[Notifications] Failed to ensure socket connection', err);
    }
  }, [resolveSocketJwtToken]);

  useEffect(() => {
    if (currentUserId) return;
    const timer = window.setInterval(() => {
      const id = resolveCurrentUserId();
      if (id) {
        setCurrentUserId(id);
        window.clearInterval(timer);
      }
    }, 1000);
    return () => window.clearInterval(timer);
  }, [currentUserId, resolveCurrentUserId]);

  useEffect(() => {
    ensureShellSocketConnected();

    const onVisible = () => {
      if (document.visibilityState === 'visible') {
        ensureShellSocketConnected();
      }
    };

    document.addEventListener('visibilitychange', onVisible);
    window.addEventListener('focus', ensureShellSocketConnected);
    return () => {
      document.removeEventListener('visibilitychange', onVisible);
      window.removeEventListener('focus', ensureShellSocketConnected);
    };
  }, [ensureShellSocketConnected]);


  // Keep the latest isOpen / currentUserId in refs so the realtime listeners can
  // be bound ONCE and still read fresh values, instead of re-binding (and only
  // re-attaching on open/close) every time those change. (T2-6356)
  const isOpenRef = useRef(isOpen);
  useEffect(() => { isOpenRef.current = isOpen; }, [isOpen]);
  const currentUserIdRef = useRef<number | null>(currentUserId);
  useEffect(() => { currentUserIdRef.current = currentUserId; }, [currentUserId]);

  const triggerLivePulse = useCallback(() => {
    if (livePulseTimer.current) {
      clearTimeout(livePulseTimer.current);
    }
    if (livePulseFrame.current) {
      cancelAnimationFrame(livePulseFrame.current);
    }

    // Remove and re-add the class so every live notification restarts the animation,
    // even when the bell already had unread notifications.
    setIsLivePulsing(false);
    livePulseFrame.current = requestAnimationFrame(() => {
      setIsLivePulsing(true);
      livePulseTimer.current = setTimeout(() => setIsLivePulsing(false), 7000);
    });
  }, []);

  const showLiveNotificationPopup = useCallback((notification: NotificationRecord) => {
    if (liveToastTimer.current) {
      clearTimeout(liveToastTimer.current);
    }
    setLiveToast(notification);
    liveToastTimer.current = setTimeout(() => setLiveToast(null), 7000);
  }, []);

  useEffect(() => {
    unreadCountRef.current = unreadCount;
  }, [unreadCount]);

  useEffect(() => {
    return () => {
      if (livePulseTimer.current) clearTimeout(livePulseTimer.current);
      if (livePulseFrame.current) cancelAnimationFrame(livePulseFrame.current);
      if (liveToastTimer.current) clearTimeout(liveToastTimer.current);
    };
  }, []);

  const fetchUnreadCount = useCallback(async (): Promise<number | null> => {
    try {
      const res: any = await Api.getUnreadNotificationsCount();
      const nextUnread = Number(res?.unread ?? 0);
      unreadCountRef.current = nextUnread;
      setUnreadCount(nextUnread);
      return nextUnread;
    } catch (err) {
      console.error('[Notifications] Failed to load unread count', err);
      return null;
    }
  }, []);

  const normalizeDateKey = (d: string) => String(d).substring(0, 10);

  const checkShiftsAvailabilityForNotifications = useCallback(async (notifList: NotificationRecord[]) => {
    const today = dayjs().startOf('day');
    const datesToCheck = new Set<string>();

    notifList.forEach(notif => {
      if (notif.type !== 'SHIFT_CANCELLED') return;
      const payload = normalizeData(notif.data);
      const ctaDate = payload.cta?.find(a => a.params?.date)?.params?.date;
      const focusDate = payload.params?.focusDate as string | undefined;
      const raw = ctaDate || focusDate;
      if (raw) {
        const normalized = normalizeDateKey(raw);
        if (!dayjs(normalized).isBefore(today, 'day')) {
          datesToCheck.add(normalized);
        }
      }
    });

    // Always build a fresh cache object — never spread from old state.
    // This guarantees cancelled shifts stop showing the CTA on every re-open.
    const freshCache: Record<string, boolean> = {};

    if (datesToCheck.size > 0) {
      const results = await Promise.all(
        Array.from(datesToCheck).map(async date => {
          try {
            const res: any = await Api.checkShiftsAvailableForDate(date);
            return { date, hasShifts: !!res?.hasShifts };
          } catch {
            return { date, hasShifts: false };
          }
        })
      );
      results.forEach(({ date, hasShifts }) => { freshCache[date] = hasShifts; });
    }

    // Direct assignment — no functional updater so there is no risk of
    // merging with a stale previous value.
    setShiftsAvailableCache(freshCache);
  }, []);

  const fetchNotifications = useCallback(async () => {
    try {
      setLoading(true);
      setShiftsAvailableCache({});  // Optimistically clear while fetching
      const res: any = await Api.getNotifications({ limit: 50, hoursBack: 72 });
      const list = Array.isArray(res?.notifications) ? res.notifications : [];
      const normalized = list.map((item: NotificationRecord) => ({
        ...item,
        isRead: !!item.isRead,
        data: normalizeData(item.data),
      }));
      setNotifications(normalized);
      setError(null);
      await checkShiftsAvailabilityForNotifications(normalized);
    } catch (err) {
      console.error('[Notifications] Failed to load list', err);
      setError('Unable to load notifications right now.');
    } finally {
      setLoading(false);
    }
  }, [checkShiftsAvailabilityForNotifications]);

  useEffect(() => {
    fetchUnreadCount();
    const onVisible = () => {
      if (document.visibilityState === 'visible') fetchUnreadCount();
    };
    document.addEventListener('visibilitychange', onVisible);
    return () => document.removeEventListener('visibilitychange', onVisible);
  }, [fetchUnreadCount]);

  useEffect(() => {
    const handleOutsidePointer = (event: MouseEvent | TouchEvent) => {
      if (!isOpen) return;
      const target =
        'changedTouches' in event && event.changedTouches[0]
          ? (event.changedTouches[0].target as Node)
          : (event.target as Node);
      if (!target) return;
      if (wrapperRef.current?.contains(target)) return;
      if (panelRef.current?.contains(target)) return;
      setIsOpen(false);
    };
    document.addEventListener('mousedown', handleOutsidePointer);
    document.addEventListener('touchstart', handleOutsidePointer, { passive: true });
    return () => {
      document.removeEventListener('mousedown', handleOutsidePointer);
      document.removeEventListener('touchstart', handleOutsidePointer);
    };
  }, [isOpen]);

  useLayoutEffect(() => {
    if (!isOpen) {
      setPanelFixedStyle(null);
      return;
    }

    const updatePosition = () => {
      const el = bellButtonRef.current;
      if (!el) return;
      const rect = el.getBoundingClientRect();
      const margin = 16;
      const vw = window.visualViewport?.width ?? window.innerWidth;
      const vh = window.visualViewport?.height ?? window.innerHeight;
      const preferredMaxWidth = vw < 430 ? 320 : 360;
      // Keep the panel visually attached to the bell on phones by sizing it to
      // the space that actually exists to the bell's left, instead of forcing
      // a near-full-width card that must be clamped back to the left edge.
      const w = Math.min(
        preferredMaxWidth,
        vw - margin * 2,
        rect.right - margin
      );
      let left = rect.right - w;
      left = Math.max(margin, Math.min(left, vw - w - margin));
      if (vw < 430) {
        // Small mobile-only visual nudge so the panel sits a bit more to the
        // right under the bell without affecting larger screens.
        left = Math.min(left + 20, vw - w - margin);
      }
      const top = rect.bottom + margin;
      const maxPanel = Math.max(200, vh - top - margin);
      setPanelFixedStyle({
        position: 'fixed',
        top,
        left,
        width: w,
        maxHeight: maxPanel,
        zIndex: 1500,
      });
    };

    updatePosition();
    window.addEventListener('resize', updatePosition);
    window.addEventListener('orientationchange', updatePosition);
    document.addEventListener('scroll', updatePosition, true);
    window.visualViewport?.addEventListener('resize', updatePosition);
    window.visualViewport?.addEventListener('scroll', updatePosition);
    return () => {
      window.removeEventListener('resize', updatePosition);
      window.removeEventListener('orientationchange', updatePosition);
      document.removeEventListener('scroll', updatePosition, true);
      window.visualViewport?.removeEventListener('resize', updatePosition);
      window.visualViewport?.removeEventListener('scroll', updatePosition);
      setPanelFixedStyle(null);
    };
  }, [isOpen]);

  useEffect(() => {
    const prependNotification = (rawNotification: any) => {
      if (!rawNotification) return;
      if (rawNotification.id && recentRealtimeNotificationIds.current.has(rawNotification.id)) {
        return;
      }
      if (rawNotification.id) {
        recentRealtimeNotificationIds.current.add(rawNotification.id);
        if (recentRealtimeNotificationIds.current.size > 100) {
          recentRealtimeNotificationIds.current.clear();
          recentRealtimeNotificationIds.current.add(rawNotification.id);
        }
      }
      const normalizedNotification: NotificationRecord = {
        ...rawNotification,
        isRead: !!rawNotification.isRead,
        data: normalizeData(rawNotification.data),
      };

      setUnreadCount(prev => {
        const next = prev + 1;
        unreadCountRef.current = next;
        return next;
      });
      triggerLivePulse();
      if (!isOpenRef.current) {
        showLiveNotificationPopup(normalizedNotification);
      }
      if (isOpenRef.current) {
        fetchNotifications();
        fetchUnreadCount();
        return;
      }
      setNotifications(prev => [normalizedNotification, ...prev]);
    };

    const legacyHandler = (payload: any) => {
      if (payload?.userId !== currentUserIdRef.current) return;
      prependNotification(payload?.notification);
    };

    const roomHandler = (payload: any) => {
      prependNotification(payload?.notification || payload);
    };

    const documentHandler = (event: Event) => {
      const detail = (event as CustomEvent)?.detail;
      if (!detail) return;
      if (detail.userId !== undefined && detail.userId !== currentUserIdRef.current) return;
      roomHandler(detail);
    };

    // RELIABLE PATH: the F7 shell (app.js) re-dispatches every socket
    // notification as a document 'inAppNotification' event. It does NOT depend
    // on the React context seeing window.TempStars.socket, so attach it
    // unconditionally — this is what makes the bell update live even when the
    // micro-frontend mounts before the shell's socket exists. (T2-6356)
    document.addEventListener('inAppNotification', documentHandler);

    // BEST-EFFORT PATH: bind directly to the shell socket too (deduped by id).
    // window.TempStars.socket may not exist yet when this runs and is re-created
    // on reconnect (forceNew), so poll for it and (re)bind when the instance
    // changes — never give up after a single miss the way the old effect did.
    let boundSocket: any = null;
    const joinNotificationRoom = (sock: any) => {
      const userId = currentUserIdRef.current;
      // The API validates room joins from the socket JWT and joins
      // user:<verifiedId> even when no userId is supplied. Do not block live
      // bell updates on the legacy TempStars.User snapshot being hydrated.
      sock?.emit?.('joinRoom', userId ? { userId } : {});
    };
    const onConnect = () => joinNotificationRoom(boundSocket);
    const unbindFrom = (sock: any) => {
      if (!sock) return;
      sock.off?.('userNotification', legacyHandler);
      sock.off?.('newInAppNotification', roomHandler);
      sock.off?.('connect', onConnect);
      sock.off?.('reconnect', onConnect);
    };
    const bindTo = (sock: any) => {
      boundSocket = sock;
      sock.on?.('userNotification', legacyHandler);
      sock.on?.('newInAppNotification', roomHandler);
      sock.on?.('connect', onConnect);
      sock.on?.('reconnect', onConnect);
      joinNotificationRoom(sock);
    };
    const acquireSocket = () => {
      const sock = (window as any)?.TempStars?.socket;
      if (sock && sock !== boundSocket) {
        unbindFrom(boundSocket);
        bindTo(sock);
      }
    };
    acquireSocket();
    document.addEventListener('tempstars:socket-ready', acquireSocket);

    return () => {
      document.removeEventListener('tempstars:socket-ready', acquireSocket);
      unbindFrom(boundSocket);
      document.removeEventListener('inAppNotification', documentHandler);
    };
  }, [currentUserId, fetchNotifications, fetchUnreadCount, showLiveNotificationPopup, triggerLivePulse]);

  const toggleDropdown = async () => {
    if (!isOpen) {
      await Promise.all([fetchNotifications(), fetchUnreadCount()]);
      if (unreadCount > 0) {
        // Mark read on the backend and clear the badge, but keep the local isRead
        // state as-is so unread highlights are still visible during this session.
        // On next open, fetchNotifications() will return them as read from the API.
        setUnreadCount(0);
        Api.markAllNotificationsRead().catch(err =>
          console.error('[Notifications] Failed to mark all read on open', err)
        );
      }
    }
    setIsOpen(prev => !prev);
  };

  const markAllRead = async () => {
    try {
      await Api.markAllNotificationsRead();
      setNotifications(prev => prev.map(item => ({ ...item, isRead: true })));
      setUnreadCount(0);
    } catch (err) {
      console.error('[Notifications] Failed to mark all read', err);
    }
  };

  const markReadLocally = (notificationId: number) => {
    setNotifications(prev =>
      prev.map(item =>
        item.id === notificationId ? { ...item, isRead: true } : item
      )
    );
  };

  const removeLocally = (notificationId: number) => {
    setNotifications(prev => prev.filter(item => item.id !== notificationId));
  };

  const incrementallyDecrement = () => {
    setUnreadCount(prev => (prev > 0 ? prev - 1 : 0));
  };

  const showSnackbar = useCallback((message: string, duration = 3000) => {
    if (snackbarTimer.current) clearTimeout(snackbarTimer.current);
    setSnackbar(message);
    snackbarTimer.current = setTimeout(() => setSnackbar(null), duration);
  }, []);

  const guardJobNotificationNavigation = useCallback(
    async (
      route?: string,
      params?: Record<string, any>,
      notificationJobId?: number | null,
    ): Promise<{ allowed: false } | { allowed: true; route?: string }> => {
      const jobId = extractNotificationJobId(route, params, notificationJobId);
      if (!jobId) {
        return { allowed: true, route };
      }
      try {
        const job = await Api.getJobDetails(jobId);
        if (isJobUnavailableForNotificationNav(job, route)) {
          showSnackbar(SHIFT_UNAVAILABLE_MESSAGE);
          return { allowed: false };
        }
        // T2-6761: stale offer notifications must not open Job Details once booked
        return {
          allowed: true,
          route: resolveDentistOfferNotificationRoute(route, job) || route,
        };
      } catch (err) {
        // T2-7517: "phone not verified" already opened the verification flow (Api.ts).
        if (isPhoneNotVerifiedError(err)) {
          return { allowed: false };
        }
        console.error('[Notifications] Job guard failed for jobId', jobId, err);
        showSnackbar(SHIFT_UNAVAILABLE_MESSAGE);
        return { allowed: false };
      }
    },
    [showSnackbar],
  );

  const navigateToRoute = useCallback(
    async (route: string | undefined, params: Record<string, any> | undefined, isCurrentNavigation: () => boolean) => {
      if (!isCurrentNavigation()) return;
      switch (route) {
        case 'confirmedOffer':
          if (params?.jobId) {
            history.push({ pathname: 'confirmedOffer', state: { jobId: params.jobId, fromNotification: true } });
          }
          break;
        case 'availableJobs':
          history.push({ pathname: 'availableJobs', state: { date: params?.date, withDate: params?.withDate } });
          break;
        case 'jobPostDetails':
          if (params?.hireJobId) {
            try {
              const job = await Api.getPublicHireJobByIdForHygienist(params.hireJobId);
              if (!isCurrentNavigation()) return;
              history.push({ pathname: 'jobPostDetails', state: { job, alertSource: params.src } });
            } catch (err) {
              console.error('[Notifications] Failed to load job posting', err);
            }
          }
          break;
        case 'hygienistMessenger':
          if (params?.jobId) {
            try {
              const job = await Api.getJobDetails(params.jobId);
              if (!isCurrentNavigation()) return;
              history.push({ pathname: 'hygienistMessenger', state: { job } });
            } catch (err) {
              console.error('[Notifications] Failed to load chat job', err);
            }
          }
          break;
        case 'hygienistHome':
          history.push({ pathname: 'hygienistHome', state: params || {} });
          break;
        case 'dentistCustomOffers':
          if (params?.jobId) {
            history.push({ pathname: 'dentistCustomOffers', state: { id: params.jobId, fromNotification: true } });
            document.dispatchEvent(new CustomEvent('jobPosted:refresh'));
          }
          break;
        case 'jobPosted':
          if (params?.jobId) {
            history.push({ pathname: 'jobPosted', state: { id: params.jobId, fromNotification: true } });
            document.dispatchEvent(new CustomEvent('jobPosted:refresh'));
          }
          break;
        case 'dentistJobConfirmed':
          if (params?.jobId) {
            history.push({
              pathname: 'dentistJobConfirmed',
              state: { id: params.jobId, fromNotification: true },
            });
          }
          break;
        case 'invoice':
          if (params?.jobId) {
            history.push({ pathname: 'invoice', state: { jobId: params.jobId } });
          }
          break;
        case 'paymentInformation':
        case 'invoiceDispute': {
          const currentUser = (window as any)?.TempStars?.User?.getCurrentUser?.();
          const key = route === 'invoiceDispute' ? 'disputeId' : 'invoiceId';
          const id = Number(params?.[key]);
          if (currentUser?.hygienistId && !currentUser?.dentistId && !currentUser?.isAdmin
              && Number.isSafeInteger(id) && id > 0) {
            history.push({ pathname: route, state: { ...params, [key]: id } });
          }
          break;
        }
        case 'talentPayouts': {
          const currentUser = (window as any)?.TempStars?.User?.getCurrentUser?.();
          if (currentUser?.hygienistId && !currentUser?.dentistId && !currentUser?.isAdmin) {
            history.push({ pathname: 'hygienistProfile', state: { focusExpressPayouts: true } });
          }
          break;
        }
        case 'messenger':
          if (params?.jobId) {
            try {
              const job = await Api.getJobDetails(params.jobId);
              if (!isCurrentNavigation()) return;
              history.push({ pathname: 'messenger', state: { job } });
            } catch (err) {
              console.error('[Notifications] Failed to load dentist messenger job', err);
            }
          }
          break;
        default:
          if (route) {
            history.push({ pathname: route, state: params || {} });
          }
      }
    },
    [history]
  );

  const handleNotificationClick = async (notification: NotificationRecord, action?: NotificationCTA) => {
    const payload = normalizeData(notification.data);
    const route = action?.route || payload.route;
    const params = action?.params || payload.params;
    const navigationActorId = (window as any)?.TempStars?.User?.getCurrentUser?.()?.id;
    const sameNavigationActor = () => !!navigationActorId
      && (window as any)?.TempStars?.User?.getCurrentUser?.()?.id === navigationActorId;

    if (String(notification.type || '').toUpperCase().startsWith('INVOICE_DISPUTE_')) {
      const actor = (window as any)?.TempStars?.User?.getCurrentUser?.();
      const allowed = route === 'invoiceDispute'
        ? actor?.hygienistId && !actor?.dentistId && !actor?.isAdmin
        : route === 'invoice' && actor?.dentistId && !actor?.hygienistId && !actor?.isAdmin;
      const targetId = Number(route === 'invoiceDispute' ? params?.disputeId : params?.jobId);
      if (!allowed || !Number.isSafeInteger(targetId) || targetId <= 0) return;
    }

    if (!notification.isRead) {
      try {
        await Api.markNotificationRead(notification.id);
        markReadLocally(notification.id);
        incrementallyDecrement();
      } catch (err) {
        console.error('[Notifications] Failed to mark notification read', err);
      }
    }

    const normalizedType = String(notification.type || '').toUpperCase();
    const isTalent = !!(window as any)?.TempStars?.User?.getCurrentUser?.()?.hygienistId;
    if (!action && isTalent && SHIFT_CANCELLED_SNACKBAR_TYPES.has(normalizedType)) {
      setIsOpen(false);
      showSnackbar('Shift no longer available');
      return;
    }

    if (route) {
      if (!sameNavigationActor()) return;
      const guard = await guardJobNotificationNavigation(route, params, notification.jobId);
      if (!guard.allowed || !sameNavigationActor()) {
        setIsOpen(false);
        return;
      }
      await navigateToRoute(guard.route || route, params, sameNavigationActor);
      setIsOpen(false);
    }
  };

  const handleSnooze = async (e: React.MouseEvent, notificationId: number) => {
    e.stopPropagation();
    try {
      // Remove from list immediately for UI responsiveness
      removeLocally(notificationId);
      // Call backend to snooze for 1 hour
      await Api.snoozeNotification(notificationId, 60); 
    } catch (err) {
      console.error('[Notifications] Failed to snooze', err);
    }
  };

  const groupedNotifications = useMemo(() => {
    const groups: Record<string, NotificationRecord[]> = {};
    notifications.forEach((notif) => {
      const label = getGroupLabel(notif.createdAt);
      if (!groups[label]) groups[label] = [];
      groups[label].push(notif);
    });
    return Object.entries(groups)
      .map(([label, items]) => ({
        label,
        items: [...items].sort(
          (a, b) => dayjs(b.createdAt).valueOf() - dayjs(a.createdAt).valueOf()
        ),
      }))
      .sort(
        (a, b) =>
          dayjs(b.items[0].createdAt).valueOf() -
          dayjs(a.items[0].createdAt).valueOf()
      );
  }, [notifications]);

  const showPanel = isOpen && panelFixedStyle !== null;
  const liveToastPayload = liveToast ? normalizeData(liveToast.data) : {};

  const panelContent = showPanel && panelFixedStyle && (
    <div
      ref={panelRef}
      className={styles.dropdown}
      style={panelFixedStyle}
    >
          <div className={styles.dropdownHeader}>
            <div>
              <h4>Notifications</h4>
              <span className={styles.headerSubtitle}>More coming soon with the ability to customize.</span>
            </div>
            {notifications.some(n => !n.isRead) && (
              <button type="button" className={styles.markAll} onClick={markAllRead}>
                Mark all as read
              </button>
            )}
          </div>
          <div className={styles.list}>
            {error && <div className={styles.error}>{error}</div>}
            {loading && <div className={styles.emptyState}>Loading...</div>}
            {!loading && notifications.length === 0 && (
              <div className={styles.emptyState}>
                No notifications yet!
              </div>
            )}
            {!loading && groupedNotifications.map(({ label, items }) => (
              <React.Fragment key={label}>
                <div className={styles.groupHeader}>{label}</div>
                {items.map((notif) => {
                  const payload = normalizeData(notif.data);
                  const distinctPreview = getDistinctPreview(notif, payload);
                  const isAdminBroadcast = isAdminBroadcastNotification(notif, payload);
                  const displayMessage = getNotificationMessage(notif, payload);
                  return (
                    <div
                      key={notif.id}
                      className={`${styles.item} ${!notif.isRead ? styles.unread : ''} ${
                        isAdminBroadcast ? styles.adminBroadcastItem : ''
                      }`}
                      onClick={() => handleNotificationClick(notif)}
                    >
                      {getIconForType(notif.type, payload.color)}

                      <div className={styles.contentColumn}>
                        <div className={styles.titleRow}>
                          <div className={styles.title}>{notif.title}</div>
                          <div className={styles.time}>{formatTime(notif.createdAt)}</div>
                        </div>

                        {isAdminBroadcast ? (
                          <AdminBroadcastMessage message={displayMessage} />
                        ) : (
                          notif.message && <div className={styles.message}>{notif.message}</div>
                        )}

                        {distinctPreview && !isAdminBroadcast && (
                          <div className={styles.previewBox}>{distinctPreview}</div>
                        )}

                        {payload.cta && payload.cta.length > 0 && (() => {
                          const isShiftCancelled = notif.type === 'SHIFT_CANCELLED';
                          const visibleCtas = payload.cta.filter(action => {
                            const date = action.params?.date;
                            if (!date) return true;
                            const dateKey = normalizeDateKey(date);
                            if (dayjs(dateKey).isBefore(dayjs(), 'day')) return false;
                            if (isShiftCancelled && action.route === 'availableJobs') {
                              return shiftsAvailableCache[dateKey] === true;
                            }
                            return true;
                          });
                          return visibleCtas.length > 0 ? (
                            <div className={styles.ctaContainer}>
                              {visibleCtas.map((action, idx) => (
                                <span
                                  key={idx}
                                  className={styles.ctaLink}
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    handleNotificationClick(notif, action);
                                  }}
                                >
                                  {action.label}
                                </span>
                              ))}
                            </div>
                          ) : null;
                        })()}
                      </div>

                      {payload.snoozeable && (
                        <button
                          type="button"
                          className={styles.snoozeBtn}
                          title="Snooze for 1 hour"
                          onClick={(e) => handleSnooze(e, notif.id)}
                        >
                          ⏰
                        </button>
                      )}
                    </div>
                  );
                })}
              </React.Fragment>
            ))}
          </div>
        </div>
  );

  return (
    <div className={styles.wrapper} ref={wrapperRef}>
      <button
        ref={bellButtonRef}
        type="button"
        className={`${styles.bellButton} ${unreadCount > 0 ? styles.bellGlow : ''} ${isLivePulsing ? styles.bellLivePulse : ''}`}
        onClick={toggleDropdown}
        aria-label="Notifications"
      >
        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
          <path d="M18 8a6 6 0 0 0-12 0c0 7-3 9-3 9h18s-3-2-3-9" />
          <path d="M13.73 21a2 2 0 0 1-3.46 0" />
        </svg>
        {unreadCount > 0 && (
          <span className={styles.badge}>
            <span className={styles.badgeIcon}>!</span>
          </span>
        )}
      </button>

      {showPanel && typeof document !== 'undefined' && panelContent
        ? (createPortal(panelContent, document.body) as unknown as React.ReactNode)
        : null}

      {liveToast && (
        <button
          type="button"
          className={styles.liveToast}
          onClick={() => {
            const notification = liveToast;
            if (liveToastTimer.current) clearTimeout(liveToastTimer.current);
            setLiveToast(null);
            handleNotificationClick(notification);
          }}
        >
          {getIconForType(liveToast.type, liveToastPayload.color)}
          <span className={styles.liveToastContent}>
            <strong>{liveToast.title || 'New notification'}</strong>
            <span>{getNotificationMessage(liveToast, liveToastPayload)}</span>
          </span>
        </button>
      )}

      {snackbar && (
        <div className={styles.snackbar} role="status" aria-live="polite">
          {snackbar}
        </div>
      )}
    </div>
  );
};

export default NotificationBell;
